import * as XLSX from 'xlsx';
import mammoth from 'mammoth/mammoth.browser';

export const MOMENT_TYPES = [
  'Goal scored', 'Goal against',
  'Big chance created', 'Big chance conceded', 'Key chance',
  'Build-up error', 'Pressing success', 'Press broken',
  'Turnover', 'Counterattack',
  'Set-piece attack', 'Set-piece defense',
  'Defensive organization', 'Defensive mistake',
  'Attacking transition',
  'Substitution concern', 'Fatigue concern', 'Player role issue',
  'Momentum shift', 'Game-state management issue',
  'Other'
];

export const IMPACT_OPTIONS = ['Low', 'Medium', 'High', 'Match-changing'];
export const ALLOWED_IMPORT_EXTENSIONS = ['xlsx', 'csv', 'txt', 'docx'];
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

const ALLOWED_MIME_TYPES = {
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/octet-stream', ''],
  csv: ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain', 'application/octet-stream', ''],
  txt: ['text/plain', 'application/octet-stream', ''],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/zip', 'application/octet-stream', ''],
};

function tinyHash(input) {
  const text = typeof input === 'string'
    ? input
    : Array.from(new Uint8Array(input || new ArrayBuffer(0))).map(b => String.fromCharCode(b)).join('');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function bytesStartWith(bytes, signature) {
  if (bytes.length < signature.length) return false;
  return signature.every((value, index) => bytes[index] === value);
}

async function validateFileContent(file, extension, arrayBufferOrText) {
  const bytes = typeof arrayBufferOrText === 'string'
    ? new TextEncoder().encode(arrayBufferOrText.slice(0, 16))
    : new Uint8Array(arrayBufferOrText || new ArrayBuffer(0));
  const zipLike = bytesStartWith(bytes, [0x50, 0x4b, 0x03, 0x04]) || bytesStartWith(bytes, [0x50, 0x4b, 0x05, 0x06]) || bytesStartWith(bytes, [0x50, 0x4b, 0x07, 0x08]);
  if ((extension === 'xlsx' || extension === 'docx') && !zipLike) {
    throw new Error(`${extension.toUpperCase()} import rejected: file content is not a valid Office Open XML document.`);
  }
  if ((extension === 'csv' || extension === 'txt') && bytes.includes(0x00)) {
    throw new Error(`${extension.toUpperCase()} import rejected: file appears to be binary or malformed text.`);
  }
  if ((extension === 'csv' || extension === 'txt') && zipLike) {
    throw new Error(`${extension.toUpperCase()} import rejected: file content looks like a renamed Office document.`);
  }
}

async function readFileAsArrayBuffer(file) {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsArrayBuffer(file);
  });
}

async function readFileAsText(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsText(file);
  });
}

const POSITIVE_TYPES = new Set(['Goal scored', 'Big chance created', 'Pressing success', 'Set-piece attack', 'Counterattack', 'Attacking transition']);
const NEGATIVE_TYPES = new Set(['Goal against', 'Big chance conceded', 'Build-up error', 'Press broken', 'Defensive mistake', 'Set-piece defense']);
const GOAL_TYPES = new Set(['Goal scored', 'Goal against']);

export const BLANK_IMPORT_PREVIEW = {
  source: { fileName: '', extension: '', size: 0 },
  match: {},
  keyMoments: [],
  roleReviews: [],
  evidence: {},
  skippedRows: [],
  warnings: [],
  errors: [],
};

export function blankMoment() {
  return {
    minute: '', moment_type: '', what_happened: '', coach_observation: '',
    impact: 'Medium', phase_of_play: '', video_timestamp: '', evidence_notes: '',
    team_unit: '', player_or_role: '', perspective: '', tags: [],
    perspective_source: 'none', impact_source: 'none', video_r2_key: ''
  };
}

export function isCompletelyEmptyMoment(moment = {}) {
  return !(
    (moment.minute || '').trim() ||
    (moment.moment_type || '').trim() ||
    (moment.what_happened || '').trim() ||
    (moment.coach_observation || '').trim() ||
    (moment.phase_of_play || '').trim() ||
    (moment.video_timestamp || '').trim() ||
    (moment.evidence_notes || '').trim() ||
    (moment.team_unit || '').trim() ||
    (moment.player_or_role || '').trim() ||
    (moment.perspective || '').trim() ||
    (moment.tags || []).length > 0 ||
    ((moment.impact || '') && moment.impact !== 'Medium')
  );
}

export function normalizeText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function normalizeKey(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function normalizePerspective(value) {
  const key = normalizeKey(value).replace(/\s+/g, '');
  if (['forus', 'for', 'ours', 'ourteam'].includes(key)) return 'for_us';
  if (['againstus', 'against', 'opponent', 'opposition'].includes(key)) return 'against_us';
  if (key === 'neutral') return 'neutral';
  return '';
}

export function normalizeImpact(value) {
  const key = normalizeKey(value).replace(/\s+/g, ' ');
  if (key === 'low') return 'Low';
  if (key === 'medium') return 'Medium';
  if (key === 'high') return 'High';
  if (key === 'match changing' || key === 'matchchanging' || key === 'critical') return 'Match-changing';
  return '';
}

export function inferMomentType(description = '') {
  const text = normalizeText(description).toLowerCase();
  if (text.includes('goal') && !text.includes('against')) return 'Goal scored';
  if (text.includes('goal against') || text.includes('conceded')) return 'Goal against';
  if (text.includes('chance')) return 'Big chance created';
  if (text.includes('press')) return 'Pressing success';
  if (text.includes('mistake') || text.includes('error')) return 'Defensive mistake';
  return 'Other';
}

export function normalizeMomentType(value) {
  const text = normalizeKey(value);
  const exact = MOMENT_TYPES.find(type => normalizeKey(type) === text);
  if (exact) return exact;
  if (/goal\s*(against|conceded|allowed)/i.test(value)) return 'Goal against';
  if (/goal/i.test(value)) return 'Goal scored';
  if (/(big|clear)\s+chance\s+(conceded|against)/i.test(value)) return 'Big chance conceded';
  if (/(big|clear|key)\s+chance/i.test(value)) return value.toLowerCase().includes('key') ? 'Key chance' : 'Big chance created';
  if (/build.?up.*(error|mistake|loss)/i.test(value)) return 'Build-up error';
  if (/press.*(broken|failed|beat)/i.test(value)) return 'Press broken';
  if (/press/i.test(value)) return 'Pressing success';
  if (/set.?piece.*(def|against|conceded)/i.test(value)) return 'Set-piece defense';
  if (/set.?piece/i.test(value)) return 'Set-piece attack';
  if (/defensive.*(organization|shape|block)/i.test(value)) return 'Defensive organization';
  if (/defensive.*(mistake|error)/i.test(value)) return 'Defensive mistake';
  if (/counter/i.test(value)) return 'Counterattack';
  if (/turnover/i.test(value)) return 'Turnover';
  if (/fatigue/i.test(value)) return 'Fatigue concern';
  if (/substitut/i.test(value)) return 'Substitution concern';
  if (/momentum/i.test(value)) return 'Momentum shift';
  return '';
}

function isDecisiveGoalDescription(text = '') {
  const value = normalizeText(text).toLowerCase();
  return /late equalizer|extra[-\s]?time goal|winning goal|penalty equalizer|final decisive goal|last[-\s]?minute winner|stoppage[-\s]?time winner/.test(value);
}

export function applySmartDefaults(moment) {
  const m = { ...moment };
  const description = m.what_happened || m.description || '';
  const perspectiveSource = m.perspective_source || (m.perspective ? 'import' : 'none');
  const impactSource = m.impact_source || ((m.impact && m.impact !== 'Medium') ? 'import' : 'none');

  if (!m.moment_type) {
    const inferredType = normalizeMomentType(`${description} ${m.notes || ''}`);
    if (inferredType) {
      m.moment_type = inferredType;
      m.moment_type_source = 'inferred';
    }
  }

  if (!m.perspective && perspectiveSource !== 'user' && perspectiveSource !== 'import') {
    if (m.moment_type === 'Goal scored') {
      m.perspective = 'for_us';
      m.perspective_source = 'inferred';
    } else if (m.moment_type === 'Goal against') {
      m.perspective = 'against_us';
      m.perspective_source = 'inferred';
    } else if (POSITIVE_TYPES.has(m.moment_type)) {
      m.perspective = 'for_us';
      m.perspective_source = 'inferred';
    } else if (NEGATIVE_TYPES.has(m.moment_type)) {
      m.perspective = 'against_us';
      m.perspective_source = 'inferred';
    }
  } else {
    m.perspective_source = perspectiveSource;
  }

  if ((!m.impact || m.impact === 'Medium') && impactSource !== 'user' && impactSource !== 'import') {
    if (GOAL_TYPES.has(m.moment_type) && isDecisiveGoalDescription(description)) {
      m.impact = 'Match-changing';
      m.impact_source = 'suggested';
    } else if (GOAL_TYPES.has(m.moment_type)) {
      m.impact = 'High';
      m.impact_source = 'inferred';
    } else {
      m.impact = m.impact || 'Medium';
      m.impact_source = impactSource;
    }
  } else {
    m.impact_source = impactSource;
  }

  return m;
}

export function applyAllSmartDefaults(moments = []) {
  return (moments || []).map(moment => {
    const m = { ...moment };
    const description = m.what_happened || m.description || '';
    const originalPerspective = m.perspective;
    const originalImpact = m.impact;

    if (!m.moment_type) {
      m.moment_type = inferMomentType(description);
    }

    if (!originalPerspective) {
      if (m.moment_type === 'Goal scored') {
        m.perspective = 'for_us';
        m.perspective_source = 'inferred';
      } else if (m.moment_type === 'Goal against') {
        m.perspective = 'against_us';
        m.perspective_source = 'inferred';
      } else if (POSITIVE_TYPES.has(m.moment_type)) {
        m.perspective = 'for_us';
        m.perspective_source = 'inferred';
      } else if (NEGATIVE_TYPES.has(m.moment_type)) {
        m.perspective = 'against_us';
        m.perspective_source = 'inferred';
      }
    }

    const isMatchChangingDesc = isDecisiveGoalDescription(description);
    if (isMatchChangingDesc && (!originalImpact || originalImpact === 'Medium')) {
      m.impact = 'Match-changing';
      m.impact_source = 'suggested';
    } else if (!originalImpact || originalImpact === 'Medium') {
      if (m.moment_type === 'Goal scored' || m.moment_type === 'Goal against') {
        m.impact = 'High';
        m.impact_source = 'inferred';
      } else {
        m.impact = originalImpact || 'Medium';
      }
    }
    return m;
  });
}

export function importFingerprint(moment = {}) {
  return [
    normalizeKey(moment.minute),
    normalizeKey(moment.moment_type),
    normalizeKey(moment.perspective),
    normalizeKey(moment.impact),
    normalizeKey(moment.what_happened),
  ].join('|');
}

export function importStableIdentity(moment = {}) {
  if (moment.import_source_id && moment.import_source_section && moment.import_source_row) {
    return [
      normalizeKey(moment.import_source_id),
      normalizeKey(moment.import_source_section),
      normalizeKey(moment.import_source_row),
      normalizeKey(moment.minute),
      normalizeKey(moment.moment_type),
    ].join('|');
  }
  return '';
}

function duplicateIdentity(moment = {}) {
  return importStableIdentity(moment) || importFingerprint(moment);
}

export function roleReviewFingerprint(review = {}) {
  return [
    normalizeKey(review.player_name),
    normalizeKey(review.position_or_role),
  ].join('|');
}

export function roleReviewStableIdentity(review = {}) {
  if (review.import_source_id && review.import_source_section && review.import_source_row) {
    return [
      normalizeKey(review.import_source_id),
      normalizeKey(review.import_source_section),
      normalizeKey(review.import_source_row),
      normalizeKey(review.player_name),
    ].join('|');
  }
  return '';
}

function reviewIdentity(review = {}) {
  return roleReviewStableIdentity(review) || roleReviewFingerprint(review);
}

export function detectDuplicateRoleReviews(existing = [], incoming = []) {
  const existingKeys = new Set(existing.map(reviewIdentity).filter(Boolean));
  const seenIncoming = new Set();
  const duplicateIndexes = new Set();
  incoming.forEach((review, index) => {
    const key = reviewIdentity(review);
    if (existingKeys.has(key) || seenIncoming.has(key)) duplicateIndexes.add(index);
    seenIncoming.add(key);
  });
  return {
    duplicateIndexes,
    duplicates: incoming.filter((_, i) => duplicateIndexes.has(i)),
    newReviews: incoming.filter((_, i) => !duplicateIndexes.has(i)),
  };
}

export function analyzeDraftMomentCleanup(moments = []) {
  const emptyIndexes = [];
  const duplicateIndexes = [];
  const duplicateGroups = [];
  const seenFingerprints = new Map();
  moments.forEach((moment, index) => {
    if (isCompletelyEmptyMoment(moment)) {
      emptyIndexes.push(index);
      return;
    }
    const fingerprint = duplicateIdentity(moment);
    if (seenFingerprints.has(fingerprint)) {
      duplicateIndexes.push(index);
      const group = duplicateGroups.find(g => g.fingerprint === fingerprint);
      if (group) group.indexes.push(index);
      else {
        const firstIndex = seenFingerprints.get(fingerprint);
        duplicateGroups.push({ fingerprint, originalIndex: firstIndex, indexes: [index] });
      }
    } else {
      seenFingerprints.set(fingerprint, index);
    }
  });
  return { emptyIndexes, duplicateIndexes, duplicateGroups, removableIndexes: [...emptyIndexes, ...duplicateIndexes].sort((a, b) => a - b) };
}

export function applyDraftMomentCleanup(moments = []) {
  const cleanup = analyzeDraftMomentCleanup(moments);
  const remove = new Set(cleanup.removableIndexes);
  return moments.filter((_, index) => !remove.has(index));
}

export function isMeaningfulMoment(moment = {}) {
  return !!(
    normalizeText(moment.minute) &&
    normalizeText(moment.perspective) &&
    normalizeText(moment.what_happened)
  );
}

export function validateMomentsForReport(moments = []) {
  const submitted = moments
    .map((moment, index) => ({ moment, index }))
    .filter(({ moment }) => !isCompletelyEmptyMoment(moment));
  const incomplete = submitted.filter(({ moment }) => !isMeaningfulMoment(moment));
  return {
    submittedCount: submitted.length,
    validCount: submitted.length - incomplete.length,
    incompleteCount: incomplete.length,
    incompleteIndexes: incomplete.map(({ index }) => index),
    readyForReport: submitted.length > 0 && incomplete.length === 0,
  };
}

export function computeMomentSummary(moments = []) {
  const readiness = validateMomentsForReport(moments);
  const submitted = moments.filter(m => !isCompletelyEmptyMoment(m));
  return {
    total: moments.length,
    submittedCount: readiness.submittedCount,
    missingPerspective: submitted.filter(m => !normalizeText(m.perspective)).length,
    missingDescription: submitted.filter(m => !normalizeText(m.what_happened)).length,
    missingCoachObs: submitted.filter(m => !normalizeText(m.coach_observation)).length,
    incompleteRows: readiness.incompleteCount,
    incompleteCount: readiness.incompleteCount,
    incompleteIndexes: readiness.incompleteIndexes,
    readyCount: readiness.validCount,
    readyForReport: readiness.readyForReport,
  };
}

export function detectDuplicateMoments(existing = [], incoming = []) {
  const existingKeys = new Set(existing.map(duplicateIdentity).filter(Boolean));
  const seenIncoming = new Set();
  const duplicateIndexes = new Set();
  incoming.forEach((moment, index) => {
    const key = duplicateIdentity(moment);
    if (existingKeys.has(key) || seenIncoming.has(key)) duplicateIndexes.add(index);
    seenIncoming.add(key);
  });
  return {
    duplicateIndexes,
    duplicates: incoming.filter((_, i) => duplicateIndexes.has(i)),
    newMoments: incoming.filter((_, i) => !duplicateIndexes.has(i)),
  };
}

// Approved precedence:
//   manual user (4) > explicit import (3) > suggested (2) > inferred (1) > empty (0)
const SOURCE_RANK = {
  user: 4, manual: 4,            // manual user choice highest
  import: 3, explicit_import: 3, // explicit value from imported file
  suggested: 2,                  // smart-default suggestion (context-aware)
  inferred: 1,                   // basic inference / automatic default
  none: 0, '': 0,               // empty / unknown
};

function normalizeSource(src) {
  if (!src) return '';
  const s = String(src).trim().toLowerCase();
  if (s === 'manual') return 'user';
  if (s === 'explicit_import') return 'import';
  if (s === 'none') return '';
  return s;
}

function sourceRank(src) {
  return SOURCE_RANK[normalizeSource(src)] ?? 0;
}

// mergeMoment: merges two moments using source-rank precedence.
// Returns null if the merge would create a material conflict between equal-ranked explicit values.
function mergeMoment(base = {}, incoming = {}, { allowConflict = false } = {}) {
  const merged = { ...base };

  // Description: richer (longer non-empty) wins
  const richerDesc = normalizeText(incoming.what_happened || '').length > normalizeText(base.what_happened || '').length
    ? incoming.what_happened : base.what_happened;
  merged.what_happened = richerDesc || incoming.what_happened || base.what_happened || '';

  // Other text fields: fill only if base is blank
  for (const field of ['coach_observation', 'evidence_notes', 'video_timestamp', 'phase_of_play', 'team_unit', 'player_or_role']) {
    if (!normalizeText(merged[field]) && normalizeText(incoming[field])) merged[field] = incoming[field];
  }

  // Perspective: source-rank arbitrates; equal high-rank conflict → flag
  const basePersp = sourceRank(base.perspective_source);
  const inPersp   = sourceRank(incoming.perspective_source);
  if (!base.perspective) {
    // Empty base always takes incoming value
    if (incoming.perspective) {
      merged.perspective = incoming.perspective;
      merged.perspective_source = normalizeSource(incoming.perspective_source) || 'import';
    }
  } else if (inPersp > basePersp) {
    // Higher-ranked incoming replaces lower-ranked base
    merged.perspective = incoming.perspective;
    merged.perspective_source = normalizeSource(incoming.perspective_source);
  } else if (inPersp === basePersp && inPersp >= 3 && incoming.perspective && incoming.perspective !== base.perspective) {
    // Equal explicit ranks with different values → conflict (caller handles)
    if (!allowConflict) return null; // signal conflict
    // allowConflict: keep base
  }
  // else: lower-ranked incoming → keep base (do nothing)

  // Impact: source-rank arbitrates; treat blank/unset Medium as rank-0
  const baseImp = sourceRank(base.impact_source);
  const inImp   = sourceRank(incoming.impact_source);
  const baseImpBlank = !base.impact || (base.impact === 'Medium' && baseImp === 0);
  if (baseImpBlank) {
    if (incoming.impact) {
      merged.impact = incoming.impact;
      merged.impact_source = normalizeSource(incoming.impact_source) || 'import';
    }
  } else if (inImp > baseImp) {
    merged.impact = incoming.impact;
    merged.impact_source = normalizeSource(incoming.impact_source);
  } else if (inImp === baseImp && inImp >= 3 && incoming.impact && incoming.impact !== base.impact) {
    if (!allowConflict) return null; // signal conflict
    // allowConflict: keep base
  }
  // else: lower-ranked incoming → keep base

  // Tags: union
  merged.tags = Array.from(new Set([...(merged.tags || []), ...(incoming.tags || [])].filter(Boolean)));

  return applySmartDefaults(merged);
}

// descriptionSimilarity: Sørensen-Dice bigram similarity with guards.
// Returns 0 if either description is absent or too short to compare reliably.
const MIN_SIMILARITY_CHARS = 12; // descriptions shorter than this cannot be auto-merged
const SIMILARITY_THRESHOLD = 0.72;

function descriptionSimilarity(a = '', b = '') {
  const na = normalizeText(a || '').toLowerCase();
  const nb = normalizeText(b || '').toLowerCase();
  // Either absent → no similarity info
  if (!na || !nb) return 0;
  // Exact match
  if (na === nb) return 1;
  // Too short to compare reliably "goal" vs "goal" would score 1 even though meaningless
  if (na.length < MIN_SIMILARITY_CHARS || nb.length < MIN_SIMILARITY_CHARS) return 0;
  // Sørensen-Dice bigram similarity
  const bigrams = str => {
    const set = new Set();
    for (let i = 0; i < str.length - 1; i++) set.add(str.slice(i, i + 2));
    return set;
  };
  const ba = bigrams(na);
  const bb = bigrams(nb);
  if (ba.size === 0 || bb.size === 0) return 0;
  let inter = 0;
  for (const g of ba) if (bb.has(g)) inter++;
  return (2 * inter) / (ba.size + bb.size);
}

// Checks whether two moments have conflicting explicit fields (player/role indicating separate events)
function hasExplicitFieldConflict(a, b) {
  const rankA_p = sourceRank(a.perspective_source);
  const rankB_p = sourceRank(b.perspective_source);
  const rankA_i = sourceRank(a.impact_source);
  const rankB_i = sourceRank(b.impact_source);
  // Conflicting explicit perspectives (both import or higher, different values)
  if (rankA_p >= 3 && rankB_p >= 3 && a.perspective && b.perspective && a.perspective !== b.perspective) return true;
  // Conflicting explicit impacts
  if (rankA_i >= 3 && rankB_i >= 3 && a.impact && b.impact && a.impact !== b.impact) return true;
  // Conflicting player/role (both non-empty and different → likely separate events)
  if (normalizeText(a.player_or_role) && normalizeText(b.player_or_role) &&
      normalizeText(a.player_or_role) !== normalizeText(b.player_or_role)) return true;
  return false;
}

function logicalMomentKey(moment = {}) {
  return [normalizeKey(moment.minute), normalizeKey(moment.moment_type || normalizeMomentType(moment.what_happened || ''))].join('|');
}

export function reconcileMomentImports(existing = [], incoming = []) {
  const cleanIncoming = (incoming || []).filter(m => !isCompletelyEmptyMoment(m));
  const base = (existing || []).filter(m => !isCompletelyEmptyMoment(m)).map(applySmartDefaults);
  const moments = [...base];
  const duplicateIndexes = new Set();
  const mergedIndexes = [];
  const conflicts = [];

  // 1. Deduplicate incoming by stable source identity
  const incomingToProcess = [];
  const seenStableIdsInIncoming = new Set();
  for (const raw of cleanIncoming) {
    const stableId = importStableIdentity(raw);
    if (stableId) {
      if (seenStableIdsInIncoming.has(stableId)) { duplicateIndexes.add(cleanIncoming.indexOf(raw)); continue; }
      seenStableIdsInIncoming.add(stableId);
    }
    incomingToProcess.push(raw);
  }

  for (const raw of incomingToProcess) {
    const candidate = applySmartDefaults(raw);
    const stableId = importStableIdentity(candidate);
    const fp = importFingerprint(candidate);

    // Path A: stable source identity match (same row from same file re-imported)
    // Even stable-source rows must surface equal-rank explicit conflicts.
    if (stableId) {
      const matchIndex = moments.findIndex(m => importStableIdentity(m) === stableId);
      if (matchIndex !== -1) {
        const merged = mergeMoment(moments[matchIndex], candidate, { allowConflict: false });
        if (merged !== null) {
          moments[matchIndex] = merged;
          mergedIndexes.push(matchIndex);
        } else {
          // Equal-rank explicit conflict on stable-source row → review queue
          conflicts.push({ minute: candidate.minute, type: candidate.moment_type, a: moments[matchIndex], b: candidate, reason: 'explicit_conflict' });
        }
        continue;
      }
    }

    // Path B: exact normalized fingerprint match → identical records, safe auto-merge
    // (fingerprint includes all five key fields; identical fingerprint means no conflict possible)
    const fpMatchIndex = moments.findIndex(m => importFingerprint(m) === fp && fp !== '||||');
    if (fpMatchIndex !== -1) {
      moments[fpMatchIndex] = mergeMoment(moments[fpMatchIndex], candidate, { allowConflict: true });
      mergedIndexes.push(fpMatchIndex);
      continue;
    }

    // Path C: minute+type logical key only auto-merge if ALL guards pass
    const logicalKey = logicalMomentKey(candidate);
    const logicalMatchIndex = moments.findIndex(m => logicalMomentKey(m) === logicalKey && logicalKey !== '|');
    if (logicalMatchIndex !== -1) {
      const current = moments[logicalMatchIndex];
      const similarity = descriptionSimilarity(current.what_happened, candidate.what_happened);
      const explicitConflict = hasExplicitFieldConflict(current, candidate);
      const canAutoMerge = similarity >= SIMILARITY_THRESHOLD && !explicitConflict;
      if (canAutoMerge) {
        // Try to merge; mergeMoment returns null on equal-rank value conflict
        const merged = mergeMoment(current, candidate, { allowConflict: false });
        if (merged !== null) {
          moments[logicalMatchIndex] = merged;
          mergedIndexes.push(logicalMatchIndex);
        } else {
          // Equal-rank explicit conflict during merge
          conflicts.push({ minute: candidate.minute, type: candidate.moment_type, a: current, b: candidate, reason: 'explicit_conflict' });
        }
      } else {
        // Materially different descriptions or explicit field conflict → require human decision
        conflicts.push({ minute: candidate.minute, type: candidate.moment_type, a: current, b: candidate, reason: 'distinct_events' });
      }
      continue;
    }

    // Path D: new moment
    moments.push(candidate);
  }

  return {
    moments,
    duplicateIndexes,
    duplicatesRemoved: duplicateIndexes.size,
    mergedCount: mergedIndexes.length,
    blanksRemoved: (incoming || []).filter(isCompletelyEmptyMoment).length,
    conflicts,
    newMoments: moments.slice(base.length),
    newCount: moments.length - base.length,
  };
}

function parseMinute(value) {
  const text = normalizeText(value);
  const match = text.match(/^(\d{1,3})(?:\s*\+\s*(\d{1,2}))?\s*['’:]?\s*(.*)$/);
  if (!match) return { minute: '', leftover: text };
  return { minute: match[2] ? `${match[1]}+${match[2]}` : match[1], leftover: normalizeText(match[3] || '') };
}

function splitDelimitedLine(line) {
  if (line.includes('|')) return line.split('|').map(v => v.trim()).filter(Boolean);
  if (line.includes('\t')) return line.split('\t').map(v => v.trim());
  return null;
}

function tokenizeFreeText(text) {
  const tokens = [];
  let remaining = normalizeText(text);
  const type = normalizeMomentType(remaining);
  if (type) {
    tokens.push({ kind: 'type', value: type });
    remaining = remaining.replace(new RegExp(type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), '').trim();
  }
  for (const typeOption of MOMENT_TYPES) {
    if (typeOption !== type && new RegExp(`\\b${typeOption.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(remaining)) {
      remaining = remaining.replace(new RegExp(typeOption.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), '').trim();
    }
  }
  const perspectiveMatch = remaining.match(/\b(for us|against us|neutral)\b/i);
  if (perspectiveMatch) {
    tokens.push({ kind: 'perspective', value: normalizePerspective(perspectiveMatch[1]) });
    remaining = remaining.replace(perspectiveMatch[0], '').trim();
  }
  const impactMatch = remaining.match(/\b(match[-\s]?changing|critical|high|medium|low)\b/i);
  if (impactMatch) {
    tokens.push({ kind: 'impact', value: normalizeImpact(impactMatch[1]) });
    remaining = remaining.replace(impactMatch[0], '').trim();
  }
  return { tokens, remaining: normalizeText(remaining) };
}

function rowToMomentFromArray(cells, rowMeta = {}) {
  const cleaned = cells.map(c => normalizeText(c));
  const { minute, leftover } = parseMinute(cleaned[0] || '');
  if (!minute) return { moment: null, reason: 'Missing minute' };

  let momentType = '';
  let perspective = '';
  let impact = '';
  const descriptionParts = [];
  let coachObservation = '';
  let phaseOfPlay = '';
  let videoTimestamp = '';
  let evidenceNotes = '';
  let teamUnit = '';
  let playerOrRole = '';
  let tags = [];

  const rest = [leftover, ...cleaned.slice(1)].filter(Boolean);

  if (rest.length === 1) {
    const extracted = tokenizeFreeText(rest[0]);
    extracted.tokens.forEach(token => {
      if (token.kind === 'type' && !momentType) momentType = token.value;
      if (token.kind === 'perspective' && !perspective) perspective = token.value;
      if (token.kind === 'impact' && !impact) impact = token.value;
    });
    if (extracted.remaining) descriptionParts.push(extracted.remaining);
  } else rest.forEach((cell, idx) => {
    const normalizedType = normalizeMomentType(cell);
    const normalizedPerspective = normalizePerspective(cell);
    const normalizedImpact = normalizeImpact(cell);
    if (!momentType && normalizedType) { momentType = normalizedType; return; }
    if (!perspective && normalizedPerspective) { perspective = normalizedPerspective; return; }
    if (!impact && normalizedImpact) { impact = normalizedImpact; return; }
    if (idx === 1 && !coachObservation && rowMeta.structured) { coachObservation = cell; return; }
    descriptionParts.push(cell);
  });

  const freeText = rest.join(' ');
  if (!momentType || !perspective || !impact) {
    const extracted = tokenizeFreeText(freeText);
    extracted.tokens.forEach(token => {
      if (token.kind === 'type' && !momentType) momentType = token.value;
      if (token.kind === 'perspective' && !perspective) perspective = token.value;
      if (token.kind === 'impact' && !impact) impact = token.value;
    });
  }

  const leftoverDescription = descriptionParts
    .map(part => tokenizeFreeText(part).remaining)
    .filter(Boolean)
    .join(' ')
    .trim();

  const moment = applySmartDefaults({
    ...blankMoment(),
    minute,
    moment_type: momentType || 'Other',
    perspective: perspective || '',
    perspective_source: perspective ? 'import' : 'none',
    impact: impact || 'Medium',
    impact_source: impact ? 'import' : 'none',
    what_happened: leftoverDescription,
    coach_observation: coachObservation,
    phase_of_play: phaseOfPlay,
    video_timestamp: videoTimestamp,
    evidence_notes: evidenceNotes,
    team_unit: teamUnit,
    player_or_role: playerOrRole,
    tags,
  });
  return { moment, reason: '' };
}

export function parseKeyMomentRows(rows = [], sourceLabel = 'Key Moments', sourceId = '') {
  const keyMoments = [];
  const skippedRows = [];
  const normalizedRows = rows.filter(row => row && row.some(cell => normalizeText(cell)));
  if (normalizedRows.length === 0) return { keyMoments, skippedRows };

  const first = normalizedRows[0].map(normalizeKey);
  const hasHeader = first.some(h => ['minute', 'moment type', 'perspective', 'impact', 'what happened'].includes(h));
  const dataRows = hasHeader ? normalizedRows.slice(1) : normalizedRows;
  const headers = hasHeader ? first : [];

  dataRows.forEach((row, index) => {
    const lineNumber = index + (hasHeader ? 2 : 1);
    if (!row.some(cell => normalizeText(cell))) {
      skippedRows.push({ source: sourceLabel, lineNumber, rawText: '', reason: 'Empty row' });
      return;
    }
    if (String(row.join('')).match(/^[|\s\-:=]+$/)) {
      skippedRows.push({ source: sourceLabel, lineNumber, rawText: row.join(' '), reason: 'Separator row' });
      return;
    }

    let moment;
    if (hasHeader) {
      const obj = {};
      headers.forEach((header, i) => { obj[header] = row[i]; });
      const type = normalizeMomentType(obj['moment type'] || obj.type || '');
      const perspective = normalizePerspective(obj.perspective || '');
      const impact = normalizeImpact(obj.impact || '');
      const parsedMinute = parseMinute(obj.minute || '').minute;
      if (!parsedMinute) {
        skippedRows.push({ source: sourceLabel, lineNumber, rawText: row.join(' | '), reason: 'Missing minute' });
        return;
      }
      moment = applySmartDefaults({
        ...blankMoment(),
        minute: parsedMinute,
        moment_type: type || 'Other',
        perspective,
        perspective_source: perspective ? 'import' : 'none',
        impact: impact || 'Medium',
        impact_source: impact ? 'import' : 'none',
        what_happened: cleanDescription(obj['what happened'] || obj.description || '', type, perspective, impact),
        coach_observation: normalizeText(obj['coach observation'] || ''),
        phase_of_play: normalizeText(obj['phase of play'] || ''),
        video_timestamp: normalizeText(obj['video timestamp'] || ''),
        evidence_notes: normalizeText(obj['evidence notes'] || ''),
        team_unit: normalizeText(obj['team unit'] || ''),
        player_or_role: normalizeText(obj['player or role'] || ''),
        tags: parseTags(obj.tags || ''),
      });
    } else {
      const result = rowToMomentFromArray(row, { structured: false });
      if (!result.moment) {
        skippedRows.push({ source: sourceLabel, lineNumber, rawText: row.join(' | '), reason: result.reason || 'Unrecognized row' });
        return;
      }
      moment = result.moment;
    }
    moment.import_source_id = sourceId || '';
    moment.import_source_section = sourceLabel;
    moment.import_source_row = String(lineNumber);
    keyMoments.push(moment);
  });

  return { keyMoments, skippedRows };
}

export function cleanDescription(description, type = '', perspective = '', impact = '') {
  let text = normalizeText(description);
  const removable = [type, impact, perspectiveLabel(perspective), perspective]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  removable.forEach(token => {
    text = text.replace(new RegExp(`\\b${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'ig'), ' ');
  });
  return normalizeText(text);
}

export function perspectiveLabel(value) {
  if (value === 'for_us') return 'For us';
  if (value === 'against_us') return 'Against us';
  if (value === 'neutral') return 'Neutral';
  return '';
}

function parseTags(value) {
  if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean);
  return normalizeText(value).split(/[;,]/).map(normalizeText).filter(Boolean);
}

function parseCsvText(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"' && quoted && next === '"') { cell += '"'; i++; continue; }
    if (ch === '"') { quoted = !quoted; continue; }
    if (ch === ',' && !quoted) { row.push(cell); cell = ''; continue; }
    if ((ch === '\n' || ch === '\r') && !quoted) {
      if (ch === '\r' && next === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = ''; continue;
    }
    cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

function parseTextRows(text) {
  return text.split(/\r?\n/).map(line => {
    const split = splitDelimitedLine(line);
    return split || [line];
  });
}

function parseKeyValueRows(rows = []) {
  const result = {};
  rows.forEach(row => {
    const cells = row || [];
    const key = normalizeText(cells[0]);
    if (!key) return;
    const valueCells = cells.slice(1).filter(v => normalizeText(v));
    if (valueCells.length >= 1) result[normalizeKey(key)] = valueCells.length === 1 ? valueCells[0] : valueCells.map(normalizeText).join(' ');
  });
  return result;
}

function normalizeDateValue(value) {
  if (!value && value !== 0) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const yyyy = String(value.getFullYear()).padStart(4, '0');
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const yyyy = String(parsed.y).padStart(4, '0');
      const mm = String(parsed.m).padStart(2, '0');
      const dd = String(parsed.d).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  const text = normalizeText(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  
  // DD/MM/YYYY
  const m1 = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) {
    const dd = m1[1].padStart(2, '0');
    const mm = m1[2].padStart(2, '0');
    const yyyy = m1[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  // MM/DD/YYYY
  
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
  return text;
}

function normalizeMatchContext(kv = {}) {
  return {
    teamNameFromFile: kv['your team'] || kv['our team'] || '',
    opponent: kv.opponent || '',
    ourScore: kv['our score'] || '',
    opponentScore: kv['opponent score'] || '',
    venue: normalizeVenue(kv.venue || ''),
    date: normalizeDateValue(kv['match date'] || kv.date || ''),
    competition: kv.competition || '',
    tacticalFocus: kv['tactical focus'] || '',
    teamFormation: kv['our formation'] || '',
    opponentFormation: kv['opponent formation'] || '',
  };
}

function normalizeVenue(value) {
  const key = normalizeKey(value);
  if (['home', 'away', 'neutral'].includes(key)) return key;
  return '';
}

function normalizeEvidence(kv = {}) {
  return {
    externalUrl: kv['external footage url'] || kv['external url'] || kv.url || '',
    externalPlatform: kv.platform || '',
    externalNotes: kv['evidence notes'] || '',
    overall: kv['overall match notes'] || kv['overall notes'] || '',
    concern: kv['main concern'] || '',
    reviewFirst: kv['review first'] || '',
    focusUnit: kv['focus unit'] || '',
  };
}

function normalizeRoleReviews(rows = [], sourceId = '') {
  const normalizedRows = rows.filter(row => row && row.some(cell => normalizeText(cell)));
  if (normalizedRows.length < 2) return [];
  const headers = normalizedRows[0].map(normalizeKey);
  return normalizedRows.slice(1).map((row, index) => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = normalizeText(row[i]); });
    return {
      player_name: obj['player name'] || obj.player || '',
      position_or_role: obj['position or role'] || obj.position || obj.role || '',
      minutes_played: obj['minutes played'] || obj.minutes || '',
      involvement_level: obj['involvement level'] || '',
      physical_condition: obj['physical condition'] || '',
      fatigue_concern: obj['fatigue concern'] || '',
      pressing_contribution: obj['pressing contribution'] || '',
      attacking_contribution: obj['attacking contribution'] || '',
      defensive_contribution: obj['defensive contribution'] || '',
      coach_concern: obj['coach concern'] || '',
      flag_for_staff_review: obj['flag for staff review'] || obj['flag for review'] || 'No',
      notes: obj.notes || '',
      positive_contribution: obj['positive contribution'] || '',
      main_concern: obj['main concern'] || '',
      repeated_issue: obj['repeated issue'] || '',
      training_focus: obj['training focus'] || '',
      confidence_level: obj['confidence level'] || '',
      decision_question: obj['decision question'] || '',
      import_source_id: sourceId || '',
      import_source_section: 'Player Reviews',
      import_source_row: String(index + 2),
    };
  });
}

export function validateImportFile(file) {
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max size is 5MB.`);
  }
  const name = file.name || '';
  const ext = name.split('.').pop().toLowerCase();
  if (!ALLOWED_IMPORT_EXTENSIONS.includes(ext)) {
    throw new Error(`Unsupported file type ".${ext}". Supported types are: .xlsx, .csv, .txt, .docx`);
  }
  const mime = file.type || '';
  const allowedMimes = ALLOWED_MIME_TYPES[ext] || [];
  if (mime && allowedMimes.length && !allowedMimes.includes(mime)) {
    throw new Error(`Unsupported MIME type "${mime}" for extension ".${ext}".`);
  }
  return { extension: ext, fileName: name, size: file.size };
}

export async function parseMatchImportFile(file) {
  const meta = validateImportFile(file);
  const hash = tinyHash(meta.fileName + meta.size);
  const result = {
    ...BLANK_IMPORT_PREVIEW,
    source: { ...meta, hash },
  };

  if (meta.extension === 'xlsx') {
    const arrayBuffer = await readFileAsArrayBuffer(file);
    await validateFileContent(file, 'xlsx', arrayBuffer);
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
    
    const contextSheet = workbook.Sheets['Match Context'];
    if (contextSheet) {
      const rows = XLSX.utils.sheet_to_json(contextSheet, { header: 1 });
      result.match = normalizeMatchContext(parseKeyValueRows(rows));
    }
    
    const momentsSheet = workbook.Sheets['Key Moments'];
    if (momentsSheet) {
      const rows = XLSX.utils.sheet_to_json(momentsSheet, { header: 1 });
      const parsed = parseKeyMomentRows(rows, 'Key Moments', hash);
      result.keyMoments = parsed.keyMoments;
      result.skippedRows = [...result.skippedRows, ...parsed.skippedRows];
    }
    
    const reviewsSheet = workbook.Sheets['Player Reviews'];
    if (reviewsSheet) {
      const rows = XLSX.utils.sheet_to_json(reviewsSheet, { header: 1 });
      result.roleReviews = normalizeRoleReviews(rows, hash);
    }
    
    const evidenceSheet = workbook.Sheets['Match Evidence'];
    if (evidenceSheet) {
      const rows = XLSX.utils.sheet_to_json(evidenceSheet, { header: 1 });
      result.evidence = normalizeEvidence(parseKeyValueRows(rows));
    }
  } else if (meta.extension === 'csv') {
    const text = await readFileAsText(file);
    await validateFileContent(file, 'csv', text);
    const rows = parseCsvText(text);
    const parsed = parseKeyMomentRows(rows, 'CSV Import', hash);
    result.keyMoments = parsed.keyMoments;
    result.skippedRows = parsed.skippedRows;
  } else if (meta.extension === 'txt') {
    const text = await readFileAsText(file);
    await validateFileContent(file, 'txt', text);
    const rows = parseTextRows(text);
    const parsed = parseKeyMomentRows(rows, 'TXT Import', hash);
    result.keyMoments = parsed.keyMoments;
    result.skippedRows = parsed.skippedRows;
  } else if (meta.extension === 'docx') {
    const arrayBuffer = await readFileAsArrayBuffer(file);
    await validateFileContent(file, 'docx', arrayBuffer);
    const { value } = await mammoth.extractRawText({ arrayBuffer });
    const rows = parseTextRows(value || '');
    const parsed = parseKeyMomentRows(rows, 'DOCX Import', hash);
    result.keyMoments = parsed.keyMoments;
    result.skippedRows = parsed.skippedRows;
  }

  return result;
}

export function parsePasteText(text = '') {
  const hash = tinyHash(text);
  const rows = parseTextRows(text);
  const parsed = parseKeyMomentRows(rows, 'Paste Preview', hash);
  return {
    ...BLANK_IMPORT_PREVIEW,
    source: { fileName: 'pasted_text', extension: 'txt', size: text.length, hash },
    keyMoments: parsed.keyMoments,
    skippedRows: parsed.skippedRows,
  };
}

export function createTemplateWorkbookBlob() {
  const wb = XLSX.utils.book_new();
  
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Field', 'Value', 'Description / Instructions'],
    ['Your team', 'France Olympic Team', 'Name of the squad you are coaching'],
    ['Opponent', 'Spain', 'Name of the opposing squad'],
    ['Our score', '3', 'Goals scored by your squad'],
    ['Opponent score', '5', 'Goals scored by opponent'],
    ['Venue', 'neutral', 'home | away | neutral'],
    ['Match date', '2024-08-09', 'YYYY-MM-DD format'],
    ['Competition', 'Olympic Finals', 'League or tournament name'],
    ['Tactical focus', 'Pressing triggers and rest defense', 'Main tactical emphasis of this analysis'],
    ['Our formation', '4-3-1-2', 'e.g. 4-3-3, 3-5-2'],
    ['Opponent formation', '4-2-3-1', 'Opponent line-up shape'],
  ]), 'Match Context');
  
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Minute', 'Moment type', 'Perspective', 'Impact', 'What happened', 'Coach observation', 'Phase of play', 'Video timestamp', 'Evidence notes', 'Team unit', 'Player or role', 'Tags'],
    ['11', 'Goal scored', 'For us', 'High', 'Millot shot from edge of the box after mistake by Tenas.', 'Good initial high press forced turnover.', 'High press', '00:11:00', 'Highlight the keeper mistake', 'Front line', 'Millot', 'goal, press'],
    ['18', 'Goal against', 'Against us', 'High', 'Fermin Lopez equalizer from transition.', 'Midfield shape collapsed, failed to track runner.', 'Defensive transition', '00:17:45', 'Show tracking failure', 'Midfield', 'Lopez', 'conceded, transition'],
  ]), 'Key Moments');
  
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Player Name', 'Position or role', 'Minutes played', 'Involvement level', 'Physical condition', 'Fatigue concern', 'Pressing contribution', 'Attacking contribution', 'Defensive contribution', 'Coach concern', 'Flag for staff review', 'Notes', 'Positive contribution', 'Main concern', 'Repeated issue', 'Training focus', 'Confidence level', 'Decision question'],
    ['Enzo Millot', 'Attacking Midfielder', '78', 'High', 'Fit', 'None', 'Strong', 'Strong', 'Acceptable', '', 'No', 'Excellent goal and energy, faded slightly second half.', 'Great shooting from distance', 'Fading physical output after 70 mins', 'None', 'Aerobic capacity maintenance', 'High', 'Can we use him deeper in build-up?'],
  ]), 'Player Reviews');
  
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Field', 'Value', 'Instructions'],
    ['External footage URL', 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', 'Link to full match or clips'],
    ['Platform', 'YouTube', 'YouTube, Hudl, Wyscout, Veo, etc.'],
    ['Evidence notes', 'Full match replay with tactical camera.', 'Notes on how to access video'],
    ['Overall match notes', 'Disappointed with conceding 5, but offensive output was highly encouraging.', 'General review of performance'],
    ['Main concern', 'Rest defense structure during high block.', 'Critical area to address'],
    ['Review first', 'Minute 18 Fermin Lopez equalizer.', 'Immediate priority video clip'],
    ['Focus unit', 'Midfield unit defensive tracking.', 'Unit that needs target training'],
  ]), 'Match Evidence');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
